"use client";

import Image from "next/image";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";

interface ErrorViewProps {
  /** Large code shown behind the heroes, e.g. "404". Omit to hide it. */
  code?: string;
  title: string;
  description: string;
  /** Action slot. When omitted, a default "Zur Startseite" link is rendered. */
  children?: React.ReactNode;
}

/**
 * Playful, on-brand error illustration used across the 404 page, the runtime
 * error boundary and the magic-link error states. The heroes sit centre-left,
 * overlapping an oversized code rendered behind them, with the message and
 * actions on the right.
 */
export function ErrorView({ code, title, description, children }: ErrorViewProps) {
  const t = useTranslations();

  return (
    <div className="flex min-h-full items-center justify-center py-8">
      <div className="flex w-full max-w-3xl flex-col items-center gap-8 px-4 text-center">
        <div className="flex shrink-0 items-center justify-center">
          <Image
            src="/error-heros.svg"
            alt=""
            width={300}
            height={200}
            priority
            // Viewport-relative width so the image + number combo scales down to
            // fit narrow screens instead of overflowing.
            className="relative z-10 h-auto w-[clamp(9rem,40vw,18rem)]"
          />
          {code && (
            <span
              aria-hidden
              // Negative margin equal to half the image width pulls the number's
              // left edge to the image's center; the flex row then centers the
              // whole image + number combination. Sizes use clamp() so the whole
              // combo shrinks to fit on small devices.
              className="pointer-events-none relative -z-10 -ml-[clamp(4.5rem,20vw,9rem)] whitespace-nowrap text-[clamp(6.5rem,30vw,16.25rem)] font-black leading-none text-primary/25 select-none"
              style={{ fontFamily: "var(--font-poppins)" }}
            >
              {code}
            </span>
          )}
        </div>

        <div className="flex flex-col items-start gap-3 text-left">
          <h1
            className="text-2xl font-bold md:text-3xl"
            style={{ fontFamily: "var(--font-poppins)" }}
          >
            {title}
          </h1>
          <p className="max-w-sm text-muted-foreground">{description}</p>
          <div className="mt-2 flex flex-wrap justify-start gap-3">
            {children ?? (
              <Button asChild>
                <Link href="/">{t("errors.home")}</Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
